/**
 * Academy AI Tutor — Word Explainer Modal.
 * Opens when student clicks "Explain" on a vocabulary item.
 */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Volume2, Loader2, Sparkles, BookOpen, Lightbulb, AlertTriangle } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function speak(text, langCode) {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  try {
    const u = new SpeechSynthesisUtterance(text);
    u.lang = langCode || 'en-US';
    u.rate = 0.95;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  } catch (_) { /* ignore */ }
}

/**
 * Props:
 *  - open: bool
 *  - onClose: fn
 *  - word: string
 *  - lessonId: string
 *  - lessonTitle: string
 *  - direction: 'fr2en' | 'en2fr'
 *  - context: string (the sentence the word appears in)
 *  - lang: 'fr' | 'en'  (UI language)
 */
export default function AcademyTutorWordModal({ open, onClose, word, lessonId, lessonTitle, direction = 'fr2en', context = '', lang = 'fr', prestored = null }) {
  const fr = false; // Course is 100% English immersive — labels always English
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const targetLangCode = direction === 'en2fr' ? 'fr-FR' : 'en-US';

  // ESC to close + body lock
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  // Use pre-stored explanation (offline) if available; otherwise fetch from the coach.
  useEffect(() => {
    if (!open || !word) return;
    setError(null);
    if (prestored && (prestored.definition || (prestored.examples && prestored.examples.length))) {
      setData(prestored);
      setLoading(false);
      return;
    }
    setData(null);
    setLoading(true);
    (async () => {
      try {
        const r = await fetch(`${BACKEND}/api/academy/tutor/explain`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            word,
            lesson_id: lessonId || '',
            lesson_title: lessonTitle || '',
            direction,
            context,
            ui_language: 'en',
          }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.detail || 'Error');
        }
        const d = await r.json();
        setData(d);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, word, lessonId, lessonTitle, direction, context, prestored]);

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[105] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        data-testid="tutor-word-modal">
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          className="bg-slate-900 border border-emerald-400/20 w-full sm:max-w-xl sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10 bg-gradient-to-r from-emerald-500/10 to-cyan-500/5">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center shrink-0">
                <Sparkles size={16} className="text-emerald-300" />
              </div>
              <div className="min-w-0">
                <div className="text-emerald-300/80 text-[10px] uppercase tracking-wider font-semibold">
                  {fr ? 'Coach JoSmartTech' : 'JoSmartTech coach'}
                </div>
                <div className="text-white font-bold text-base truncate">{word}</div>
              </div>
            </div>
            <button onClick={onClose} className="text-white/40 hover:text-white p-2 rounded-lg hover:bg-white/5 transition shrink-0" data-testid="tutor-word-close">
              <X size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {loading && (
              <div className="flex items-center justify-center py-10 text-emerald-300 text-sm">
                <Loader2 size={18} className="animate-spin mr-2" />
                {fr ? 'Le coach prépare ton explication...' : 'Coach is preparing your explanation...'}
              </div>
            )}

            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg border border-rose-400/30 bg-rose-500/10 text-rose-200 text-sm">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                {error}
              </div>
            )}

            {data && (
              <>
                {/* Translation + listen */}
                <div className="rounded-xl border border-emerald-400/20 bg-emerald-500/[0.06] p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-emerald-200/70 text-[10px] uppercase tracking-wider font-semibold mb-1">
                        {fr ? 'Traduction' : 'Translation'}
                      </div>
                      <div className="text-white font-bold text-lg">{data.translation}</div>
                      {data.pronunciation && (
                        <div className="text-emerald-200/80 text-xs italic mt-0.5">/ {data.pronunciation} /</div>
                      )}
                      {data.type && (
                        <span className="inline-block mt-1.5 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/60">{data.type}</span>
                      )}
                    </div>
                    <button onClick={() => speak(word, targetLangCode)} className="p-2.5 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/30 shrink-0" data-testid="tutor-speak">
                      <Volume2 size={16} className="text-emerald-300" />
                    </button>
                  </div>
                </div>

                {/* Definition */}
                {data.definition && (
                  <Block icon={BookOpen} title={fr ? 'Définition' : 'Definition'} tone="white">
                    <p className="text-white/85 text-sm leading-relaxed">{data.definition}</p>
                  </Block>
                )}

                {/* Examples */}
                {data.examples?.length > 0 && (
                  <Block icon={Sparkles} title={fr ? 'Exemples (du plus simple au plus avancé)' : 'Examples (easy to advanced)'} tone="white">
                    <div className="space-y-2.5">
                      {data.examples.map((ex, i) => (
                        <div key={i} className="rounded-lg border border-white/8 bg-white/[0.03] p-2.5">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="text-white font-medium text-sm">{ex.tgt}</div>
                              <div className="text-white/55 text-xs mt-0.5 italic">{ex.src}</div>
                            </div>
                            <button onClick={() => speak(ex.tgt, targetLangCode)} className="p-1.5 rounded-md hover:bg-emerald-500/20 transition shrink-0">
                              <Volume2 size={13} className="text-emerald-300" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Block>
                )}

                {/* Synonyms */}
                {data.synonyms?.length > 0 && (
                  <Block title={fr ? 'Synonymes' : 'Synonyms'} tone="white">
                    <div className="flex flex-wrap gap-1.5">
                      {data.synonyms.map((s, i) => (
                        <span key={i} className="px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-200 border border-emerald-400/20">{s}</span>
                      ))}
                    </div>
                  </Block>
                )}

                {/* Common mistake */}
                {data.common_mistake && (
                  <Block icon={AlertTriangle} title={fr ? 'Piège à éviter' : 'Common mistake'} tone="amber">
                    <p className="text-amber-100/90 text-sm leading-relaxed">{data.common_mistake}</p>
                  </Block>
                )}

                {/* Tip */}
                {data.tip && (
                  <Block icon={Lightbulb} title={fr ? 'Astuce mémo' : 'Memory tip'} tone="cyan">
                    <p className="text-cyan-100/90 text-sm leading-relaxed">{data.tip}</p>
                  </Block>
                )}

                {data.cached && (
                  <div className="text-center text-white/30 text-[10px] italic pt-1">
                    {fr ? '⚡ Réponse instantanée — déjà étudiée par d\'autres élèves' : '⚡ Instant — already studied by other students'}
                  </div>
                )}
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Block({ icon: Icon, title, tone = 'white', children }) {
  const toneStyle = {
    white: { border: 'border-white/10', bg: 'bg-white/[0.02]', label: 'text-white/55' },
    amber: { border: 'border-amber-400/25', bg: 'bg-amber-500/[0.08]', label: 'text-amber-300' },
    cyan: { border: 'border-cyan-400/25', bg: 'bg-cyan-500/[0.08]', label: 'text-cyan-300' },
  }[tone] || { border: 'border-white/10', bg: 'bg-white/[0.02]', label: 'text-white/55' };

  return (
    <div className={`rounded-xl border ${toneStyle.border} ${toneStyle.bg} p-3.5`}>
      <div className={`${toneStyle.label} text-[10px] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1.5`}>
        {Icon && <Icon size={12} />}
        {title}
      </div>
      {children}
    </div>
  );
}
